'use client';

import { Fragment, useState } from 'react';
import type { ArchitectureState, DotStatus } from '@/lib/architecture-state';

const DOT: Record<DotStatus, string> = {
  ok: 'bg-emerald-400',
  warn: 'bg-amber-300',
  fail: 'bg-red-400',
  na: 'bg-zinc-700',
};

const VALUE_TONE: Record<DotStatus, string> = {
  ok: 'text-emerald-300',
  warn: 'text-amber-300',
  fail: 'text-red-300',
  na: 'text-white/40',
};

const PILLAR_TAG: Record<string, string> = {
  profitability: 'PROFIT',
  growth: 'GROWTH',
  safety: 'SAFETY',
  shareholderreturns: 'SR',
  ShareholderReturns: 'SR',
  Profitability: 'PROFIT',
  Growth: 'GROWTH',
  Safety: 'SAFETY',
  valuation: 'VALUE',
  Valuation: 'VALUE',
};

function classify(pct: number): DotStatus {
  if (pct > 10) return 'fail';
  if (pct > 3) return 'warn';
  return 'ok';
}

function trendArrow(t: 'up' | 'down' | 'flat'): string {
  if (t === 'up') return '↑';
  if (t === 'down') return '↓';
  return '—';
}

function trendTone(t: 'up' | 'down' | 'flat'): string {
  if (t === 'up') return 'text-red-300'; // up missing % is BAD
  if (t === 'down') return 'text-emerald-300';
  return 'text-white/40';
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${m}/${d}`;
}

interface Props {
  windowDates: string[];
  rows: ArchitectureState['factorTrend'];
}

export function FactorCoverage({ windowDates, rows }: Props): React.JSX.Element {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Factor Coverage</div>
        <div className="mt-3 text-[11px] text-white/40">No audit data available.</div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">
          Factor Coverage
        </div>
        <div className="text-[10px] text-white/40">
          Worst {rows.length} factors by today&apos;s missing %
        </div>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-white/40">
              <th className="text-left font-normal pb-2 pl-1 pr-3 w-[220px]">Factor</th>
              <th className="text-left font-normal pb-2 pr-3 w-[70px]">Pillar</th>
              <th className="text-right font-normal pb-2 pr-3 w-[80px]">Today</th>
              <th className="text-center font-normal pb-2 pr-3 w-[60px]">Trend</th>
              <th className="text-left font-normal pb-2 pr-1 w-[140px]">5-day</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const status = classify(row.todayMissingPct);
              const isOpen = expanded === row.factor;
              return (
                <Fragment key={row.factor}>
                  <tr
                    className="border-t border-zinc-900 hover:bg-zinc-900/30 cursor-pointer"
                    onClick={() => setExpanded((p) => (p === row.factor ? null : row.factor))}
                  >
                    <td className="py-1.5 pl-1 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/40">{isOpen ? '▾' : '▸'}</span>
                        <span className="text-[11px] text-white/85">{row.factor}</span>
                      </div>
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className="text-[8px] uppercase tracking-wider text-white/50">
                        {row.pillar ? PILLAR_TAG[row.pillar] ?? row.pillar.toUpperCase() : '—'}
                      </span>
                    </td>
                    <td className={`py-1.5 pr-3 text-right tabular-nums ${VALUE_TONE[status]}`}>
                      {row.todayMissingPct.toFixed(1)}%
                    </td>
                    <td className={`py-1.5 pr-3 text-center ${trendTone(row.trend)}`}>
                      <span className="text-[12px]">{trendArrow(row.trend)}</span>
                      {row.fiveDayAgoMissingPct != null && (
                        <span className="ml-1 text-[9px] tabular-nums">
                          {(row.todayMissingPct - row.fiveDayAgoMissingPct >= 0 ? '+' : '') +
                            (row.todayMissingPct - row.fiveDayAgoMissingPct).toFixed(1)}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-1">
                      <div className="flex items-center gap-1">
                        {row.history.map((h) => (
                          <span
                            key={h.date}
                            title={`${h.date}: ${h.missingPct == null ? '—' : h.missingPct.toFixed(1) + '%'}`}
                            className={`inline-block w-2 h-2 rounded-full ${
                              h.missingPct == null ? DOT.na : DOT[classify(h.missingPct)]
                            }`}
                            aria-hidden
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-zinc-950/60 border-t border-zinc-900">
                      <td colSpan={5} className="px-4 py-3 text-[10px] text-white/70">
                        <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">
                          Per-day missing %
                        </div>
                        <div className="flex items-center gap-4 tabular-nums">
                          {windowDates.map((d) => {
                            const cell = row.history.find((h) => h.date === d);
                            const pct = cell?.missingPct;
                            return (
                              <div key={d} className="flex flex-col items-center gap-0.5">
                                <span className="text-[9px] text-white/40">{shortDate(d)}</span>
                                <span
                                  className={`text-[11px] ${
                                    pct == null ? 'text-white/40' : VALUE_TONE[classify(pct)]
                                  }`}
                                >
                                  {pct == null ? '—' : pct.toFixed(1) + '%'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
